import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../utils/api';

// Fetch professionals
export const fetchProfessionals = createAsyncThunk(
  'professional/fetchProfessionals',
  async ({ search = '', page = 1, pageSize = 10 }) => {
    const response = await api.get('/professional/professionals', {
      params: { search, page, pageSize },
    });
    return response.data.data;
  }
);

// Create collaboration request
export const createCollaborationRequest = createAsyncThunk(
  'professional/createCollaborationRequest',
  async ({ professionalId, issueId, requestMessage }) => {
    const response = await api.post('/professional/collaboration-requests', {
      professionalId,
      issueId,
      requestMessage,
    });
    return response.data.data;
  }
);

// Fetch collaboration requests for a professional (admin/pro view)
export const fetchCollaborationRequests = createAsyncThunk(
  'professional/fetchCollaborationRequests',
  async (professionalId) => {
    const response = await api.get(`/professional/professionals/${professionalId}/collaboration-requests`);
    return response.data.data;
  }
);

// Fetch collaboration requests submitted by the logged-in citizen
export const fetchMyCollaborationRequests = createAsyncThunk(
  'professional/fetchMyCollaborationRequests',
  async () => {
    const response = await api.get('/professional/collaboration-requests/mine');
    return response.data.data;
  }
);

// Fetch single collaboration request
export const fetchCollaborationRequestById = createAsyncThunk(
  'professional/fetchCollaborationRequestById',
  async (requestId) => {
    const response = await api.get(`/professional/collaboration-requests/${requestId}`);
    return response.data.data;
  }
);

// Respond to collaboration request
export const respondToCollaborationRequest = createAsyncThunk(
  'professional/respondToCollaborationRequest',
  async ({ requestId, response, responseMessage }) => {
    const apiResponse = await api.patch(`/professional/collaboration-requests/${requestId}/respond`, {
      response,
      responseMessage,
    });
    return apiResponse.data.data;
  }
);

// Update collaboration request status
export const updateCollaborationRequestStatus = createAsyncThunk(
  'professional/updateCollaborationRequestStatus',
  async ({ requestId, status, responseMessage }) => {
    const response = await api.patch(`/professional/collaboration-requests/${requestId}/status`, {
      status,
      responseMessage,
    });
    return response.data.data;
  }
);

const initialState = {
  professionals: [],
  totalCount: 0,
  page: 1,
  pageSize: 10,
  status: 'idle',
  error: null,
  collaborationRequests: [],
  selectedRequest: null,
  responseStatus: 'idle',
  responseError: null,
};

const professionalSlice = createSlice({
  name: 'professional',
  initialState,
  reducers: {
    clearSelectedRequest: (state) => {
      state.selectedRequest = null;
    },
    clearResponseStatus: (state) => {
      state.responseStatus = 'idle';
      state.responseError = null;
    },
    setSelectedRequest: (state, action) => {
      state.selectedRequest = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch professionals
      .addCase(fetchProfessionals.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchProfessionals.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.professionals = action.payload.professionals;
        state.totalCount = action.payload.totalCount;
        state.page = action.payload.page;
        state.pageSize = action.payload.pageSize;
      })
      .addCase(fetchProfessionals.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message ?? null;
      })
      // Create collaboration request
      .addCase(createCollaborationRequest.fulfilled, (state, action) => {
        state.collaborationRequests.unshift(action.payload);
      })
      // Fetch collaboration requests
      .addCase(fetchCollaborationRequests.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchCollaborationRequests.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.collaborationRequests = action.payload;
      })
      .addCase(fetchCollaborationRequests.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message ?? null;
      })
      .addCase(fetchMyCollaborationRequests.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchMyCollaborationRequests.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.collaborationRequests = action.payload;
      })
      .addCase(fetchMyCollaborationRequests.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message ?? null;
      })
      // Fetch single collaboration request
      .addCase(fetchCollaborationRequestById.fulfilled, (state, action) => {
        state.selectedRequest = action.payload;
      })
      // Respond to collaboration request
      .addCase(respondToCollaborationRequest.pending, (state) => {
        state.responseStatus = 'loading';
        state.responseError = null;
      })
      .addCase(respondToCollaborationRequest.fulfilled, (state, action) => {
        state.responseStatus = 'succeeded';
        // Update the request in the list
        const index = state.collaborationRequests.findIndex(req => req.id === action.payload.id);
        if (index !== -1) {
          state.collaborationRequests[index] = action.payload;
        }
        // Update selected request if it matches
        if (state.selectedRequest && state.selectedRequest.id === action.payload.id) {
          state.selectedRequest = action.payload;
        }
      })
      .addCase(respondToCollaborationRequest.rejected, (state, action) => {
        state.responseStatus = 'failed';
        state.responseError = action.error.message ?? null;
      })
      // Update collaboration request status
      .addCase(updateCollaborationRequestStatus.pending, (state) => {
        state.responseStatus = 'loading';
        state.responseError = null;
      })
      .addCase(updateCollaborationRequestStatus.fulfilled, (state, action) => {
        state.responseStatus = 'succeeded';
        // Update the request in the list
        const index = state.collaborationRequests.findIndex(req => req.id === action.payload.id);
        if (index !== -1) {
          state.collaborationRequests[index] = action.payload;
        }
        // Update selected request if it matches
        if (state.selectedRequest && state.selectedRequest.id === action.payload.id) {
          state.selectedRequest = action.payload;
        }
      })
      .addCase(updateCollaborationRequestStatus.rejected, (state, action) => {
        state.responseStatus = 'failed';
        state.responseError = action.error.message ?? null;
      });
  },
});

export const { clearSelectedRequest, clearResponseStatus, setSelectedRequest } = professionalSlice.actions;
export default professionalSlice.reducer;